import React from 'react';
import { Device } from '../types';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';

/**
 * FS-443 — per-device filter pressure thresholds.
 *
 * Shared by both device-edit surfaces (the DeviceList modal and the
 * route-bound Settings panel) so the two cannot drift.
 *
 * Both fields are optional. Blank means unset, which is not the same as zero:
 * an unset end of life runs the device on the fleet default, and an unset
 * notification point leaves it on the pipeline's thickness-derived percentage
 * rule. The number inputs therefore emit `null` for an empty string rather
 * than coercing to 0 the way the generic device fields do.
 *
 * Validation is the server's job — it is the only place that can compare a
 * threshold against the stored baseline. What is enforced here is the
 * explanation: the operator sees the device's measured baseline and the
 * default it is currently running on, so a rejected value is not a surprise.
 */

/** Mirrors DEFAULT_END_OF_LIFE_PA in device-data-api and the daily pipeline. */
export const DEFAULT_END_OF_LIFE_PA = 125;

interface FilterThresholdFieldsProps {
  device: Pick<Device, 'filter_eol_pa' | 'filter_notify_pa' | 'filter_start_dp'>;
  onChange: (patch: Partial<Device>) => void;
  disabled?: boolean;
}

const FilterThresholdFields: React.FC<FilterThresholdFieldsProps> = ({
  device,
  onChange,
  disabled = false,
}) => {
  const eol = device.filter_eol_pa ?? null;
  const notify = device.filter_notify_pa ?? null;
  const baseline = device.filter_start_dp ?? null;
  const effectiveEol = eol ?? DEFAULT_END_OF_LIFE_PA;

  const handle = (name: 'filter_eol_pa' | 'filter_notify_pa') => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;
    onChange({ [name]: raw === '' ? null : Number(raw) } as Partial<Device>);
  };

  return (
    <div className="mb-4 border-t pt-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">Filter Pressure Thresholds</h3>
      <p className="text-xs text-muted-foreground mb-3">
        Leave blank to use the defaults. Changing a threshold applies to the next daily
        calculation — historical filter-life values are not recomputed.
        {baseline !== null && (
          <> This device&rsquo;s measured baseline is {baseline.toFixed(1)} Pa.</>
        )}
      </p>

      <div className="mb-3">
        <Label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="filter_eol_pa"
        >
          End of Life (Pa):
        </Label>
        <Input
          id="filter_eol_pa"
          type="number"
          name="filter_eol_pa"
          value={eol ?? ''}
          placeholder={`${DEFAULT_END_OF_LIFE_PA} (default)`}
          onChange={handle('filter_eol_pa')}
          disabled={disabled}
          aria-describedby="filter-eol-help"
        />
        <p id="filter-eol-help" className="text-xs text-muted-foreground mt-1">
          Filter life reaches 0% at this pressure. Must be above the measured baseline.
          Lower it for weak or low-static systems where 125 Pa is never reached.
        </p>
      </div>

      <div>
        <Label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="filter_notify_pa"
        >
          Notify At (Pa):
        </Label>
        <Input
          id="filter_notify_pa"
          type="number"
          name="filter_notify_pa"
          value={notify ?? ''}
          placeholder="unset — use filter-life percentage"
          onChange={handle('filter_notify_pa')}
          disabled={disabled}
          aria-describedby="filter-notify-help"
        />
        <p id="filter-notify-help" className="text-xs text-muted-foreground mt-1">
          Pressure at which the replacement alert fires. Must be below{' '}
          {effectiveEol} Pa. The gap to end of life is the warning margin — at a typical
          1 Pa/day of loading, {effectiveEol} and {effectiveEol - 5} give about five days
          of notice. Blank keeps the default percentage-of-life trigger.
        </p>
      </div>
    </div>
  );
};

export default FilterThresholdFields;
